import {
    forwardRef,
    useImperativeHandle,
    useRef,
    useState,
    useContext,
    useEffect,
} from 'react'

import DataList from '../../../../../../Components/DataList'
import AddressAndDateInput from '../../../../../../Components/AddressAndDateInput'

import ApplicantDataFormContext from '../../../../../../Contexts/ApplicantDataFormContext'
import StatusIcon from '../../../../../../Components/Common/StatusIcon'

import ExpandableComponentContext from '../../../../../../Contexts/ExpandableComponentContext'

const AddressHistory = forwardRef(({ title, arrayOf, data, isSelected, handleFieldSelection }, ref) => {

    const applicantDataForm = useContext(ApplicantDataFormContext)
    const currentAddress = applicantDataForm.current["identity_information.current_address"]

    const dataListRef = useRef()
    const [isOpen, setIsOpen] = useState(isSelected)
    const [error, setError] = useState(false)
    const [completed, setCompleted] = useState(false)

    const fieldData = data || {
        addressHistory: [],
    }

    fieldData.currentAddress = currentAddress

    useEffect(() => {
        setIsOpen(isSelected)
    }, [isSelected])

    const handleValidity = () => {
        let errorText
        const elementResults = dataListRef.current.validateAndGetData()
        let isValid = elementResults.isValid

        if (!currentAddress) {
            isValid = false
        }

        if (isValid) {
            const allIntervals = [currentAddress, ...elementResults.value].map(address => address.dates_of_residency)

            allIntervals.sort((a, b) => {
                // Convert to year and month numbers for comparison
                const aYear = Number(a.to.year)
                const aMonth = Number(a.to.month)
                const bYear = Number(b.to.year)
                const bMonth = Number(b.to.month)

                // Compare by year first, then by month
                if (bYear !== aYear) {
                    return bYear - aYear // Descending by year
                } else {
                    return bMonth - aMonth // Descending by month
                }
            })

            for (let i = 0; i < allIntervals.length - 1; i++) {
                const currentInterval = allIntervals[i]
                const nextInterval = allIntervals[i + 1]

                const currentFromMonths = Number(currentInterval.from.month) + (Number(currentInterval.from.year) * 12)
                const nextToMonths = Number(nextInterval.to.month) + (Number(nextInterval.to.year) * 12)

                if (currentFromMonths > nextToMonths + 1) {
                    errorText = "Please ensure all time intervals are in the correct order and there are no gaps between them."
                    isValid = false
                    break
                }
            }

            if (isValid) {
                const lastInterval = allIntervals[allIntervals.length - 1]
                const earliestTime = Number(lastInterval.from.year) * 12 + Number(lastInterval.from.month)
                const now = new Date()
                const currentTime = now.getMonth() + 1 + now.getFullYear() * 12

                // at least 10 years
                isValid = currentTime - 12 * 7 >= earliestTime

                if (!isValid) {
                    errorText = "Please provide the Addresses where you have lived, in the Past 7 years"
                }
            }
        }

        return [isValid, elementResults.value, errorText]
    }

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {

            const [
                isValid,
                resultsValue,
                errorText,
            ] = handleValidity()

            setError(errorText)
            setCompleted(isValid)

            return {
                isValid,
                value: {
                    currentAddress,
                    addressHistory: resultsValue,
                },
            }
        },
    }))

    const handleOpen = () => {
        if (isOpen) {

            const [
                isValid,
            ] = handleValidity()

            setCompleted(isValid)
        }
        if (isSelected) {
            setIsOpen(!isOpen)
            return
        }
        handleFieldSelection()
    }


    return (
        <div className="mb-4">
            <button
                onClick={handleOpen}
                className="w-full flex justify-between items-center py-2 px-0"
            >
                <span className="text-lg font-semibold">{title}</span>
                <span className="pr-4 flex items-center gap-1">
                    <StatusIcon isOpen={isOpen} completed={completed} />
                    {isOpen ? '▲' : '▼'}
                </span>
            </button>
            <div className={`${!isOpen ? 'hidden' : ''} pt-2`}>
                <ExpandableComponentContext.Provider value={{
                    isInteracted: isOpen
                }}>
                    {
                        currentAddress && (
                            <AddressAndDateInput
                                title={"Current Address"}
                                data={currentAddress}
                                attributes={arrayOf.attributes}
                                readOnly={true}
                            />
                        )
                    }
                    <DataList
                        ref={dataListRef}
                        attributes={arrayOf.attributes}
                        data={fieldData.addressHistory}
                        title={"Address"}
                        addButtonText={"Add a different Address where you have lived"}
                    />
                    {error && (
                        <p className="mt-1 text-sm text-red-600">{error}</p>
                    )}
                </ExpandableComponentContext.Provider>

            </div>
        </div>
    )
})

export default AddressHistory
